import { useRef, useState, useEffect, useId } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Disclosure — animated expand/collapse.
 *
 * Two flavours:
 *   - Standalone toggle button with caret (default)
 *   - Controlled via `open` + `onOpenChange` for managed state
 *
 * Use this for: evidence sections, "view all", supplementary detail.
 * Do NOT use this for primary navigation — sidebar groups have their own.
 */
export function Disclosure({
    title,
    summary,
    children,
    defaultOpen = false,
    open: controlledOpen,
    onOpenChange,
    trailing,
    testId,
}) {
    const isControlled = controlledOpen !== undefined;
    const [innerOpen, setInnerOpen] = useState(defaultOpen);
    const open = isControlled ? controlledOpen : innerOpen;
    const setOpen = (v) => {
        if (!isControlled) setInnerOpen(v);
        onOpenChange?.(v);
    };

    const contentRef = useRef(null);
    const [maxH, setMaxH] = useState(0);
    const id = useId();

    useEffect(() => {
        if (!contentRef.current) return;
        if (open) {
            setMaxH(contentRef.current.scrollHeight);
        } else {
            setMaxH(0);
        }
    }, [open, children]);

    return (
        <div data-testid={testId}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                aria-controls={id}
                className="w-full flex items-center justify-between gap-3 py-2.5 text-left text-[14px] text-stone-700 hover:text-stone-900 transition group"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <ChevronDown
                        size={14}
                        strokeWidth={2}
                        className={`text-stone-400 transition-transform duration-200 flex-shrink-0 ${
                            open ? "rotate-0" : "-rotate-90"
                        }`}
                    />
                    <span className="font-medium truncate">{title}</span>
                    {summary && (
                        <span className="text-stone-500 text-[13px] truncate">
                            {summary}
                        </span>
                    )}
                </div>
                {trailing}
            </button>
            <div
                id={id}
                ref={contentRef}
                className="disclosure-content"
                style={{
                    maxHeight: `${maxH}px`,
                    opacity: open ? 1 : 0,
                }}
                aria-hidden={!open}
            >
                <div className="pt-2 pb-3 pl-6">{children}</div>
            </div>
        </div>
    );
}
